import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, absoluteUrl } from "@/lib/api";
import { ArrowUpRight, ArrowRight, ImageSquare } from "@phosphor-icons/react";

const TYPE_LABELS = {
  logo: "Logos",
  flyer: "Flyers",
  business_1pager: "Web",
  artist_booking_page: "Web",
  large_product_store: "Web",
  web: "Web",
  web_update: "Web",
  poster: "Posters",
  social: "Social",
  packaging: "Packaging",
  brochure: "Brochures",
  business_card: "Business Cards",
  photography: "Photography",
  video: "Video",
  other: "Other",
};

export function labelFor(serviceType) {
  if (!serviceType) return "Other";
  return TYPE_LABELS[serviceType] || serviceType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function PortfolioCard({ item }) {
  return (
    <div data-testid={`work-${item.id}`} className="group bg-[#0a0a0a] flex flex-col">
      <Link to={`/work/${item.id}`} className="aspect-[4/3] bg-[#0f0f0f] relative overflow-hidden block" data-testid={`work-link-${item.id}`}>
        {item.cover_url ? (
          <img
            src={`${absoluteUrl(item.cover_url)}?w=800`}
            alt={item.title}
            loading="lazy"
            decoding="async"
            width="800"
            height="600"
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#444]">
            <ImageSquare size={32} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60 group-hover:opacity-90 transition-opacity" />
        <div className="absolute top-3 left-3 flex gap-1.5">
          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-white/30 bg-black/60 text-white">
            {labelFor(item.service_type)}
          </span>
          {item.platform && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FF2D2D] bg-[#FF2D2D] text-white" data-testid={`work-platform-${item.id}`}>
              {item.platform}
            </span>
          )}
        </div>
      </Link>
      <div className="p-5 flex flex-col flex-1">
        <Link to={`/work/${item.id}`} className="font-display text-lg leading-tight truncate hover:text-[#FF2D2D] transition-colors">
          {item.title}
        </Link>
        {item.client_name && item.client_name !== item.title && (
          <div className="text-xs text-[#a3a3a3] mt-1 uppercase tracking-wider truncate">For {item.client_name}</div>
        )}
        {item.description && (
          <p className="text-sm text-[#a3a3a3] mt-3 line-clamp-3 leading-relaxed" data-testid={`work-desc-${item.id}`}>{item.description}</p>
        )}
        {item.external_url && (
          <a
            href={item.external_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`work-visit-${item.id}`}
            className="mt-auto pt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#fafafa] hover:text-[#FF2D2D] transition-colors self-start"
          >
            Visit site <ArrowUpRight size={14} weight="bold" />
          </a>
        )}
      </div>
    </div>
  );
}

export function PortfolioSection({ limit = null, teaser = false }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portfolio");
        setItems(data || []);
      } catch (_) {}
      setLoaded(true);
    })();
  }, []);

  const tabs = useMemo(() => {
    const grouped = new Map();
    items.forEach((it) => {
      const lbl = labelFor(it.service_type);
      grouped.set(lbl, (grouped.get(lbl) || 0) + 1);
    });
    return [
      { id: "all", label: "All", count: items.length },
      ...Array.from(grouped.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([lbl, count]) => ({ id: lbl, label: lbl, count })),
    ];
  }, [items]);

  const filtered = useMemo(() => {
    let list = items;
    if (tab !== "all") list = items.filter((it) => labelFor(it.service_type) === tab);
    return limit ? list.slice(0, limit) : list;
  }, [items, tab, limit]);

  if (teaser && (!loaded || items.length === 0)) return null;
  if (!loaded) return <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 text-[#a3a3a3]">Loading work…</div>;

  return (
    <section id="work" className={teaser ? "border-t border-[#262626] bg-[#050505]" : "bg-[#050505]"} data-testid={teaser ? "portfolio-teaser" : "portfolio-full"}>
      <div className={`max-w-[1400px] mx-auto px-6 md:px-10 ${teaser ? "py-20 md:py-28" : "pb-20 md:pb-28"}`}>
        {teaser && (
          <div className="flex items-end justify-between gap-10 flex-wrap mb-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Selected work</div>
              <h2 className="font-display text-5xl md:text-6xl leading-[0.95] mt-3">Vision Brought to Life</h2>
            </div>
            <Link
              to="/portfolio"
              data-testid="view-full-portfolio"
              className="btn-outline px-5 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2"
            >
              View full portfolio <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        )}

        {!teaser && tabs.length > 2 && (
          <div className="flex gap-1 mb-8 flex-wrap" data-testid="portfolio-tabs">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`portfolio-tab-${t.id}`}
                className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors flex items-center gap-2 ${
                  tab === t.id
                    ? "border-[#FF2D2D] bg-[#FF2D2D] text-white"
                    : "border-[#262626] text-[#a3a3a3] hover:border-[#FF2D2D] hover:text-[#FF2D2D]"
                }`}
              >
                <span>{t.label}</span>
                <span className="text-[10px] tabular-nums opacity-70">{t.count}</span>
              </button>
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="border border-dashed border-[#262626] p-12 text-center text-[#a3a3a3]" data-testid="portfolio-empty">
            New work is on the way — check back soon.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]" data-testid="portfolio-grid-public">
            {filtered.map((item) => <PortfolioCard key={item.id} item={item} />)}
          </div>
        )}
      </div>
    </section>
  );
}
